import { Label } from '@/design-system/label/label';
import { getBeneficiaryCountryDetails } from '@/pages/payments/utils/get-beneficiary-country-details';
import { getCountryFlag } from '@/utils/get-country-flag';
import { BeneficiaryCountry, BeneficiaryType, LegalEntityCountry } from '@seapoint/server/enums';
import pluralize from 'pluralize';
import { Fragment, useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { Separator } from '../../../../design-system/separator/separator';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountBankingTypeView, PayrollView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { getBankAccountName } from '../../../../utils/get-bank-account-name';
import { AccountSelectItem } from '../../../payments/components/account-select-item';
import { canInitiateBulkPayments } from '../../../payments/utils/capability-checks';
import { PayrollPaymentRecordCard } from './payroll-payment-record-card';

type BulkPayrollPaymentFormProps = {
  payroll: PayrollView;
  accounts: AccountBankingTypeView[];
  isLoading: boolean;
  onSubmit: (paymentDetails: BulkPayrollPaymentDetails) => void;
};

export type BulkPayrollPaymentDetails = {
  sourceAccountId: string;
  payrollId: string;
  totalAmount: number;
};

const getCountryName = (countryCode: BeneficiaryCountry) => {
  return getBeneficiaryCountryDetails(countryCode).name;
};

export const BulkPayrollPaymentForm = ({ accounts, payroll, isLoading, onSubmit }: BulkPayrollPaymentFormProps) => {
  const availableSourceAccounts = accounts.filter(
    (account) => canInitiateBulkPayments(account) && payroll.currency === account.currency,
  );
  const defaultSourceAccountId = availableSourceAccounts[0]?.id;
  const [selectedSourceAccountId, setSelectedSourceAccountId] = useState(defaultSourceAccountId);
  const selectedSourceAccount = availableSourceAccounts.find((a) => a.id === selectedSourceAccountId);

  const totalAmount = payroll.records.reduce((acc, record) => acc + record.netPay, 0);
  const hasInsufficientFunds = selectedSourceAccount && totalAmount > selectedSourceAccount.totalBalance;

  const handleSubmit = () => {
    if (!selectedSourceAccount) return;

    const paymentDetails: BulkPayrollPaymentDetails = {
      sourceAccountId: selectedSourceAccount.id,
      payrollId: payroll.id,
      totalAmount,
    };

    onSubmit(paymentDetails);
  };

  return (
    <div className="w-full">
      <Stack space={6} align="stretch" className="rounded-lg bg-background p-6">
        <h2 className="text-xl font-semibold">Payment</h2>
        {/* From Section */}
        <Stack space={2} align="stretch">
          <Inline space={2} align="center">
            <img
              src={getCountryFlag(payroll.country as unknown as LegalEntityCountry)}
              alt={getCountryName(payroll.country)}
              className="h-6 w-6 rounded-full object-cover"
            />
            <h3 className="font-bold">Select {payroll.currency} Account</h3>
          </Inline>
          <div
            className={`rounded-lg border bg-white ${hasInsufficientFunds ? 'border-destructive' : 'border-border'}`}
          >
            <Select
              placeholder={`Select ${payroll.currency} Account`}
              onValueChange={(value) => {
                setSelectedSourceAccountId(value);
              }}
              defaultValue={selectedSourceAccountId}
              triggerLabel={<Label className="text-xs font-medium text-muted-foreground">From</Label>}
              triggerClassName="py-3 px-4 rounded-lg border-0 bg-white text-black text-base font-medium"
              isTriggerFullWidth={true}
            >
              {availableSourceAccounts.map((account) => {
                const accountName = getBankAccountName({
                  displayName: account.displayName,
                  institutionName: account.institution?.name,
                });

                return (
                  <Select.Item key={account.id} value={account.id} className="py-3">
                    <AccountSelectItem
                      balance={account.totalBalance}
                      currency={account.currency}
                      institutionLogo={account.institution?.logo}
                      name={accountName}
                      renderBalanceInline
                    />
                  </Select.Item>
                );
              })}
            </Select>
          </div>
          {hasInsufficientFunds && <p className="text-sm font-medium text-destructive">Not enough funds on account</p>}
        </Stack>

        {/* To Section */}
        <Stack space={2} align="stretch">
          <Inline justify="between" align="center">
            <p className="text-base font-bold">To</p>
            <p className="text-sm font-bold text-muted-foreground">
              {payroll.records.length} {pluralize('recipient', payroll.records.length)}
            </p>
          </Inline>

          <div className="max-h-64 overflow-y-auto">
            <Stack align="stretch" className="relative rounded-lg bg-muted">
              {payroll.records.map((record, index) => (
                <Fragment key={record.id}>
                  <PayrollPaymentRecordCard
                    name={record.employee.firstName + ' ' + record.employee.lastName}
                    currency={payroll.currency}
                    accountDetails={
                      record.employee.destination.type === BeneficiaryType.IBAN
                        ? {
                            beneficiaryType: BeneficiaryType.IBAN,
                            iban: record.employee.destination.iban!,
                          }
                        : {
                            beneficiaryType: BeneficiaryType.SCAN,
                            accountNumber: record.employee.destination.accountNumber!,
                            sortCode: record.employee.destination.sortCode!,
                          }
                    }
                    reference={payroll.reference}
                    amount={record.netPay}
                  />
                  {index < payroll.records.length - 1 && (
                    <div className="px-[20px]">
                      <Separator />
                    </div>
                  )}
                </Fragment>
              ))}
            </Stack>
          </div>
        </Stack>

        {/* Total Net Pay */}
        <div className="py-4">
          <Inline justify="between" align="center">
            <p className="text-base font-bold text-black">Total net pay</p>
            <p className="text-xl font-bold text-black">
              {getAmountWithCurrency({
                amount: totalAmount,
                currency: payroll.currency,
                isAccountBalance: true,
              })}
            </p>
          </Inline>
        </div>

        {/* Pay Button */}
        <Button
          variant="default"
          type="submit"
          size="lg"
          onClick={handleSubmit}
          disabled={!selectedSourceAccount || isLoading || hasInsufficientFunds}
          isLoading={isLoading}
          className="w-full bg-primary text-black hover:bg-primary/90"
        >
          Pay
        </Button>
      </Stack>
    </div>
  );
};
